import { Input, message, Modal, Row, Space, Spin, Upload } from "antd";
import { useEffect, useRef, useState } from "react";
import Recorder from "recorder-core";
import { flushSync } from "react-dom";
import { RcFile } from "antd/es/upload";
import { nanoid } from "nanoid";

import "recorder-core/src/extensions/frequency.histogram.view";
import "recorder-core/src/extensions/lib.fft";
import "recorder-core/src/engine/mp3";
import "recorder-core/src/engine/mp3-engine";

import styles from "./index.less";

import { ResponseCode } from "@/constants";
import { AskAIType } from "@/utils/streamFetch";
import { postUploadFile } from "@/services/chat";
import { AllowedAccept, getAskAIType } from "../utils";

import { ReactComponent as KeyBoardIcon } from "@/assets/images/chat/input/keyboard.svg";
import { ReactComponent as VoiceIcon } from "@/assets/images/chat/input/speak.svg";
import { ReactComponent as UploadIcon } from "@/assets/images/chat/input/upload.svg";
import { ReactComponent as ClearIcon } from "@/assets/images/chat/input/clear.svg";
import { ReactComponent as MicrophoneIcon } from "@/assets/images/chat/input/microphone_icon.svg";

import { ReactComponent as SendIcon } from "@/assets/images/chat/input/send.svg";
import classNames from "classnames";

type ChatInputProps = {
  value: string;
  onEnter: (value: string, type: AskAIType, extraInfo?: any) => void;
  onChange: (value: string) => void;
};

const ChatInput = ({ value, onEnter, onChange }: ChatInputProps) => {
  const recorderRef = useRef<any>();
  const waveRef = useRef<any>();
  const [inputType, setInputType] = useState<"text" | "voice">("text");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    recorderRef.current = Recorder({
      onProcess: function (
        buffers: any,
        powerLevel: any,
        bufferDuration: any,
        bufferSampleRate: any
        // newBufferIdx: any,
      ) {
        waveRef.current?.input(
          buffers[buffers.length - 1],
          powerLevel,
          bufferSampleRate
        );
      },
    });
  }, []);

  function beginRecord() {
    waveRef.current = Recorder?.FrequencyHistogramView?.({
      elem: "#recwave",
      stripeEnable: false,
      lineCount: 6,
      position: 0,
      minHeight: 1,
      widthRatio: 0.3,
      stripeHeight: 1,
      mirrorEnable: true,
      linear: [0, "#CCAC85", 1, "#CCAC85"],
    });

    recorderRef.current.start();
  }

  /**
   * 结束录音，直接生成文件上传
   */
  async function stopSpeak() {
    const waitUrl = new Promise<string>((resolve, reject) => {
      recorderRef.current?.stop(
        async function (blob: any, duration: any) {
          console.log(
            blob,
            (window.URL || webkitURL).createObjectURL(blob),
            "时长:" + duration + "ms"
          );
          const url = (window.URL || webkitURL).createObjectURL(blob);
          console.log("本地播放链接为 ", url);
          console.log(
            "已录制mp3：" +
              duration +
              "ms " +
              blob.size +
              "字节，可以点击播放、上传了",
            2
          );

          try {
            const file = new File([blob], `record_${nanoid()}.mp3`, {
              type: blob.type,
            });
            const rsp = await postUploadFile(file);
            if (rsp.code === ResponseCode.Success) {
              if (!!rsp.data.url) {
                resolve(rsp.data.url);
              } else {
                console.error("未返回 url");
                reject();
                return;
              }
            }
          } catch (err) {
            console.error("文件上传失败", err);
            reject();
          }
        },
        function (msg: any) {
          console.log("录音失败:" + msg, 1);
          reject();
        }
      );
    });

    return await waitUrl;
  }

  async function handleUpload(file: RcFile) {
    // const { size } = file;
    // if (size > 50 * 1024 * 1024) {
    //   message.warning("文件大小不能超过50M");
    //   return;
    // }
    setUploading(true);
    const type = getAskAIType(file.type, file.name);
    try {
      const rsp = await postUploadFile(file);
      if (rsp.code === ResponseCode.Success) {
        if (!!rsp.data.url && !!type) {
          // 触发 ai 事件
          onEnter(rsp.data.url, type, { name: file.name });
        } else {
          console.error("未返回 url");
        }
      }
      setUploading(false);
    } catch (err: any) {
      setUploading(false);
      console.error(err);
    }
  }

  function handleSpeak() {
    flushSync(() => {
      setIsSpeaking(true);
    });
    beginRecord();
  }

  async function handleSendVoice() {
    setIsSpeaking(false);
    const url = await stopSpeak();
    onEnter(url, "VOICE");
  }

  function renderActionIcon() {
    if (inputType === "text") {
      return (
        <div
          className={styles.icon}
          onClick={() => {
            setInputType("voice");
            recorderRef.current.open(
              () => {
                message.success("开启麦克风成功");
              },
              function (msg: any, isUserNotAllow: any) {
                //用户拒绝未授权或不支持
                // console.log(msg, isUserNotAllow);
                if (!isUserNotAllow) {
                  Modal.error({
                    title: "错误",
                    content:
                      "无法录音，请检查是否开启录音权限，或者是否有麦克风设备",
                  });
                  stopSpeak();
                }
                setInputType("text");
              }
            );
          }}
        >
          <VoiceIcon />
        </div>
      );
    }

    return (
      <div
        className={styles.icon}
        onClick={() => {
          setIsSpeaking(false);
          stopSpeak();
          setInputType("text");
        }}
      >
        <KeyBoardIcon />
      </div>
    );
  }

  function renderSpeak() {
    if (!isSpeaking) {
      return (
        <Row
          className={styles["voice-input"]}
          justify="center"
          align="middle"
          style={{ color: "#0486FE", fontWeight: "bold" }}
          onClick={handleSpeak}
        >
          <MicrophoneIcon className={styles.microphone} />
          <div className="text-primary-300">点击向我提问</div>
        </Row>
      );
    }
    return (
      <Row
        className={`${styles["voice-input"]} ${styles["record-input"]}`}
        onClick={handleSendVoice}
      >
        提问结束 点击发送
        <div id="recwave" className={styles["recording-icon"]} />
        <div
          className={styles.clear}
          onClick={(e) => {
            e.stopPropagation();
            setIsSpeaking(false);
            stopSpeak();
          }}
        >
          <ClearIcon />
        </div>
      </Row>
    );
  }

  return (
    <div className={styles["chat-input"]}>
      {renderActionIcon()}
      {inputType === "text" && (
        <div className={styles["text-input"]}>
          <Input.TextArea
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder="请输入..."
            onKeyDown={(e) => {
              if (e.keyCode === 13 && !e.shiftKey) {
                e.preventDefault();
                if (value) {
                  onEnter(value, "TEXT");
                }
                return;
              }
            }}
            autoSize={{ minRows: 1, maxRows: 10 }}
            className={styles.textarea}
          />

          <Space align="center">
            <SendIcon
              className={classNames(
                styles["send-icon"],
                value ? "text-primary-300" : "text-[#C7C4C2]"
              )}
              onClick={() => {
                if (value) {
                  onEnter(value, "TEXT");
                }
              }}
            />
          </Space>
        </div>
      )}

      {inputType === "voice" && renderSpeak()}
      <Upload
        beforeUpload={handleUpload}
        showUploadList={false}
        maxCount={1}
        customRequest={() => {}}
        accept={AllowedAccept}
      >
        <Spin spinning={uploading}>
          <div className={styles.icon}>
            <UploadIcon />
          </div>
        </Spin>
      </Upload>
    </div>
  );
};

export default ChatInput;
